/**
 * @fileoverview Narrative quality prompt for time entries (MOR-1039)
 *
 * Purpose: flag a narrative that does not justify the time recorded, before
 * the entry goes for approval. A soft prompt only — it never blocks a save or
 * a submit, and nothing here runs server-side, so imports and AI drafts are
 * unaffected.
 *
 * The standard is ARITA's, which is qualitative. The Code of Professional
 * Practice (s 15.1, Recording of Work Done) expects a record of the "details
 * of the work being performed", and its remuneration guidance asks for
 * narrative that is "sufficient, meaningful, open and clear" and gives
 * "specifics of the work done for this particular appointment". ARITA sets no
 * word count. The thresholds below are Morgan's heuristics for that standard
 * (more time needs more explanation; stock phrases are not specifics) and
 * live here in one place so they can be tuned or made a firm setting later.
 *
 * Pure by design: no React, no Convex.
 */

/**
 * Every entry needs at least this many SPECIFIC words: words that are neither
 * stock activity words nor filler. Short entries are judged on specifics, not
 * length, because a terse narrative can be perfectly specific ("Reviewed
 * director's ROCAP" names whose document and which one) while a longer one
 * can say nothing ("Attending to various matters on file").
 */
const BASE_MIN_SPECIFIC_WORDS = 2;

/** Longer entries need more. First tier whose `minHours` is met wins. */
const PROPORTIONAL_TIERS = [
  { minHours: 3, minWords: 20 },
  { minHours: 1, minWords: 10 },
] as const;

/**
 * Words that say what kind of activity happened but nothing about this
 * appointment. A narrative built only from these (plus filler) is generic.
 */
const STOCK_WORDS: ReadonlySet<string> = new Set([
  "admin",
  "administration",
  "administrative",
  "attend",
  "attendance",
  "attended",
  "attending",
  "call",
  "called",
  "calling",
  "calls",
  "correspondence",
  "discuss",
  "discussed",
  "discussion",
  "discussions",
  "docs",
  "document",
  "documents",
  "email",
  "emailed",
  "emailing",
  "emails",
  "file",
  "files",
  "general",
  "internal",
  "job",
  "matter",
  "matters",
  "meet",
  "meeting",
  "meetings",
  "met",
  "misc",
  "miscellaneous",
  "ongoing",
  "paperwork",
  "phone",
  "phoned",
  "prep",
  "preparation",
  "prepare",
  "prepared",
  "preparing",
  "research",
  "researched",
  "review",
  "reviewed",
  "reviewing",
  "sundry",
  "task",
  "tasks",
  "time",
  "update",
  "updated",
  "updates",
  "updating",
  "various",
  "work",
  "worked",
  "working",
]);

const FILLER_WORDS: ReadonlySet<string> = new Set([
  "a",
  "about",
  "an",
  "and",
  "as",
  "at",
  "by",
  "etc",
  "follow",
  "for",
  "from",
  "in",
  "into",
  "of",
  "on",
  "re",
  "regarding",
  "the",
  "to",
  "up",
  "via",
  "with",
]);

/**
 * Spread onto every narrative input and textarea (MOR-1455): browser
 * spellcheck on, dictionary hinted to Australian English. One constant so a
 * new narrative editor cannot quietly miss it.
 */
export const NARRATIVE_INPUT_PROPS = {
  spellCheck: true,
  lang: "en-AU",
} as const;

export interface NarrativeWarning {
  kind: "generic" | "brief";
  /** Two or three words for a badge: "Generic", "Brief for 2 hours". */
  label: string;
  /** The full explanation, for a tooltip or a review list. */
  message: string;
}

const ARITA_EXPECTATION =
  "ARITA expects details of the work performed, specific to this appointment.";

function wordsOf(narrative: string): string[] {
  return narrative
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.replace(/[^a-z0-9]/g, ""))
    .filter((word) => word.length > 0);
}

function formatHours(hours: number): string {
  const rounded = Math.round(hours * 10) / 10;
  return `${rounded} ${rounded === 1 ? "hour" : "hours"}`;
}

/**
 * A warning when the narrative looks too thin for the time recorded, else
 * `null`. An empty narrative returns `null`: that is the required-field
 * rule's job, not this prompt's.
 *
 * `referenceText` is the firm's own guidance (MOR-1039) — their ARITA Code
 * references and any internal policies — appended to the message so a flagged
 * entry points at the standard the firm actually stands behind. When it is
 * missing or blank the built-in ARITA prompt is used as a fallback.
 */
export function checkNarrative(
  narrative: string,
  units: number,
  unitMinutes: number,
  referenceText?: string,
): NarrativeWarning | null {
  const reference =
    referenceText && referenceText.trim().length > 0
      ? referenceText.trim()
      : ARITA_EXPECTATION;

  const words = wordsOf(narrative);
  if (words.length === 0) return null;

  const specificWords = words.filter(
    (word) => !STOCK_WORDS.has(word) && !FILLER_WORDS.has(word),
  );
  if (specificWords.length === 0) {
    return {
      kind: "generic",
      label: "Generic",
      message: `This narrative reads as generic. ${reference}`,
    };
  }

  const hours =
    Number.isFinite(units) && units > 0 && unitMinutes > 0
      ? (units * unitMinutes) / 60
      : 0;
  const tier = PROPORTIONAL_TIERS.find((t) => hours >= t.minHours);
  if (tier && words.length < tier.minWords) {
    return {
      kind: "brief",
      label: `Brief for ${formatHours(hours)}`,
      message: `This narrative looks brief for ${formatHours(hours)}. ${reference}`,
    };
  }
  if (specificWords.length < BASE_MIN_SPECIFIC_WORDS) {
    return {
      kind: "brief",
      label: "Brief",
      message: `This narrative looks brief. ${reference}`,
    };
  }
  return null;
}
